// @ts-nocheck
import { apiClient } from './apiClient';

export interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: string;
  ipAddress: string;
  userAgent: string;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, any>;
  compliance: string[];
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  category: string;
  dataAccessed?: string[];
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
}

export interface AuditFilter {
  userId?: string;
  startDate?: Date;
  endDate?: Date;
  actions?: string[];
  resources?: string[];
  severity?: string[];
  compliance?: string[];
  success?: boolean;
  search?: string;
}

export interface AuditStats {
  totalEvents: number;
  criticalEvents: number;
  failedEvents: number;
  uniqueUsers: number;
  topActions: { action: string; count: number }[];
  complianceBreaches: number;
  timeRange: {
    start: string;
    end: string;
  };
}

export interface ComplianceReport {
  reportId: string;
  generatedAt: string;
  period: {
    start: string;
    end: string;
  };
  regulations: {
    gdpr: ComplianceMetrics;
    coppa: ComplianceMetrics;
    ferpa: ComplianceMetrics;
    ccpa: ComplianceMetrics;
  };
  violations: ComplianceViolation[];
  recommendations: string[];
}

export interface ComplianceMetrics {
  compliant: boolean;
  score: number;
  issues: string[];
  lastAudit: string;
  nextAudit: string;
}

export interface ComplianceViolation {
  id: string;
  timestamp: string;
  regulation: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  affectedUsers: number;
  resolved: boolean;
  resolutionDate?: string;
  remediation?: string;
}

export interface DataAccessLog {
  id: string;
  timestamp: string;
  accessedBy: string;
  accessedByRole: string;
  dataSubject: string;
  dataCategories: string[];
  purpose: string;
  legalBasis: string;
  retention: string;
  exported: boolean;
  shared: boolean;
  thirdParty?: string;
}

class AuditService {
  private baseUrl = '/api/v1/audit';

  // Audit Log Methods
  async getUserAuditLog(
    userId: string,
    options: {
      page?: number;
      pageSize?: number;
      filter?: string;
      sort?: string;
      order?: 'asc' | 'desc';
    } = {}
  ) {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options.filter) params.append('filter', options.filter);
    if (options.sort) params.append('sort', options.sort);
    if (options.order) params.append('order', options.order);

    const response = await apiClient.get(`${this.baseUrl}/logs/${userId}?${params.toString()}`);
    return response.data;
  }

  async getSystemAuditLogs(
    filter: AuditFilter,
    pagination: {
      page: number;
      pageSize: number;
    }
  ) {
    const response = await apiClient.post(`${this.baseUrl}/logs/system`, { filter, ...pagination });
    return response.data;
  }

  async getAuditLogById(logId: string) {
    const response = await apiClient.get(`${this.baseUrl}/logs/entry/${logId}`);
    return response.data;
  }

  async exportAuditLog(userId: string, format: 'csv' | 'json' | 'pdf', filter?: AuditFilter) {
    const response = await apiClient.post(
      `${this.baseUrl}/export/${userId}`,
      { format, filter },
      { responseType: 'blob' }
    );
    return response.data;
  }

  async exportSystemAuditLog(format: 'csv' | 'json' | 'pdf', filter: AuditFilter) {
    const response = await apiClient.post(
      `${this.baseUrl}/export/system`,
      { format, filter },
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Audit Statistics Methods
  async getAuditStats(filter: AuditFilter): Promise<AuditStats> {
    const response = await apiClient.post(`${this.baseUrl}/stats`, filter);
    return response.data;
  }

  async getUserAuditStats(userId: string, days: number = 30) {
    const response = await apiClient.get(`${this.baseUrl}/stats/${userId}?days=${days}`);
    return response.data;
  }

  async getSecurityEvents(severity: 'high' | 'critical', limit: number = 100) {
    const response = await apiClient.get(`${this.baseUrl}/security-events?severity=${severity}&limit=${limit}`);
    return response.data;
  }

  // Compliance Reporting Methods
  async generateComplianceReport(
    regulation: 'gdpr' | 'coppa' | 'ferpa' | 'ccpa' | 'all',
    startDate: string,
    endDate: string
  ): Promise<ComplianceReport> {
    const response = await apiClient.post(`${this.baseUrl}/compliance/report`, { regulation, startDate, endDate });
    return response.data;
  }

  async getComplianceViolations(filter: {
    regulation?: string;
    resolved?: boolean;
    severity?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ComplianceViolation[]> {
    const response = await apiClient.post(`${this.baseUrl}/compliance/violations`, filter);
    return response.data;
  }

  async resolveComplianceViolation(violationId: string, remediation: string) {
    const response = await apiClient.put(`${this.baseUrl}/compliance/violations/${violationId}/resolve`, {
      remediation,
    });
    return response.data;
  }

  async getComplianceScore(userId?: string) {
    const url = userId ? `${this.baseUrl}/compliance/score/${userId}` : `${this.baseUrl}/compliance/score`;
    const response = await apiClient.get(url);
    return response.data;
  }

  // Data Access Logging Methods
  async logDataAccess(access: Omit<DataAccessLog, 'id' | 'timestamp'>) {
    const response = await apiClient.post(`${this.baseUrl}/data-access`, access);
    return response.data;
  }

  async getDataAccessLogs(
    dataSubject: string,
    filter?: {
      startDate?: string;
      endDate?: string;
      accessedBy?: string;
      purpose?: string;
    }
  ): Promise<DataAccessLog[]> {
    const response = await apiClient.post(`${this.baseUrl}/data-access/${dataSubject}`, filter || {});
    return response.data;
  }

  async getDataAccessReport(userId: string, days: number = 90) {
    const response = await apiClient.get(`${this.baseUrl}/data-access/${userId}/report?days=${days}`);
    return response.data;
  }

  // Audit Trail Integrity Methods
  async verifyAuditIntegrity(startDate: string, endDate: string) {
    const response = await apiClient.post(`${this.baseUrl}/integrity/verify`, { startDate, endDate });
    return response.data;
  }

  async getAuditHash(logId: string) {
    const response = await apiClient.get(`${this.baseUrl}/integrity/hash/${logId}`);
    return response.data;
  }

  async validateAuditChain(startId: string, endId: string) {
    const response = await apiClient.post(`${this.baseUrl}/integrity/validate-chain`, { startId, endId });
    return response.data;
  }

  // Real-time Monitoring Methods
  async subscribeToAuditEvents(filter: AuditFilter, callback: (event: AuditLog) => void) {
    // WebSocket subscription for real-time audit events
    const ws = new WebSocket(`${window.location.origin.replace('http', 'ws')}/api/v1/audit/stream`);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'subscribe', filter }));
    };

    ws.onmessage = event => {
      const auditEvent = JSON.parse(event.data);
      callback(auditEvent);
    };

    return {
      unsubscribe: () => ws.close(),
    };
  }

  async getActiveAlerts() {
    const response = await apiClient.get(`${this.baseUrl}/alerts/active`);
    return response.data;
  }

  async acknowledgeAlert(alertId: string) {
    const response = await apiClient.put(`${this.baseUrl}/alerts/${alertId}/acknowledge`);
    return response.data;
  }

  // Retention and Archival Methods
  async archiveAuditLogs(beforeDate: string) {
    const response = await apiClient.post(`${this.baseUrl}/archive`, { beforeDate });
    return response.data;
  }

  async getArchivedLogs(filter: AuditFilter, archiveId: string) {
    const response = await apiClient.post(`${this.baseUrl}/archive/${archiveId}/search`, filter);
    return response.data;
  }

  async getRetentionStatus() {
    const response = await apiClient.get(`${this.baseUrl}/retention/status`);
    return response.data;
  }

  // Analytics Methods
  async getAuditAnalytics(
    metric: 'user-activity' | 'resource-access' | 'security-events' | 'compliance',
    period: 'day' | 'week' | 'month' | 'year'
  ) {
    const response = await apiClient.get(`${this.baseUrl}/analytics/${metric}?period=${period}`);
    return response.data;
  }

  async getAnomalies(timeRange: { start: string; end: string }, threshold: number = 0.8) {
    const response = await apiClient.post(`${this.baseUrl}/analytics/anomalies`, { timeRange, threshold });
    return response.data;
  }

  async getPredictiveInsights() {
    const response = await apiClient.get(`${this.baseUrl}/analytics/predictive`);
    return response.data;
  }
}

export const auditService = new AuditService();
